import React, { useCallback, useRef } from "react";
import PropTypes from "prop-types";
import * as R from "ramda";

import { Button, TextButton } from "components/Button";
import Popover from "components/Popover";
import Text from "components/Text";
import FixAnswersTooltip from "components/projects/FixAnswersTooltip";
import { useAnswerContext } from "contexts/answerContext";
import useAsyncCallbackPendingState from "hooks/useAsyncCallbackPendingState";
import useDefferedButtonHandler from "hooks/useDefferedButtonHandler";
import { toKey } from "queries/fields";
import { getErrors } from "reducers/answerContexts";

import styles from "./Footer.scss";
import FooterShell from "./FooterShell";

export const useFooterErrors = (fieldIdents) => {
  const { record: project } = useAnswerContext();
  const fieldKeys = fieldIdents.map(toKey);

  const invalidFields = R.intersection(R.keys(getErrors(project)), fieldKeys);
  const firstInvalidField = R.head(invalidFields);

  const numErrors = invalidFields.length;
  const scrollToFirstError = useCallback(() => {
    if (firstInvalidField)
      document
        .getElementById(firstInvalidField)
        .scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
  }, [firstInvalidField]);

  return {
    numErrors,
    scrollToFirstError,
  };
};

const Footer = ({
  backTextKey = "guides.footer.previous",
  disableBack = false,
  disableNext = false,
  message,
  error = null,
  isLoading = false,
  nextTextKey = "guides.footer.next",
  onBack,
  onNext,
  numErrors,
  scrollToFirstError,
}) => {
  const buttonRef = useRef();
  const { pending } = useAnswerContext();

  const [bufferedOnNext, projectSavePending] = useDefferedButtonHandler(onNext, pending);

  // tracking onNext here to unrender the FixAnswersTooltip while validations are pending, in order to ensure it shows again each time the Next button is clicked (resetting its internal close-button hiding state)
  const [onNextWithLoading, isOnNextPending] = useAsyncCallbackPendingState(bufferedOnNext);

  const showErrors = !isOnNextPending && (error || numErrors > 0);

  return (
    <>
      {showErrors &&
        (error ? (
          <Popover
            placement="top-end"
            referenceElement={buttonRef.current}
            className={styles.errorBox}
          >
            <Text t={error.textKey} {...error.textProps} />
          </Popover>
        ) : (
          <FixAnswersTooltip
            referenceElement={buttonRef.current}
            numErrors={numErrors}
            scrollToFirstError={scrollToFirstError}
          />
        ))}
      <FooterShell className={styles.footer}>
        <div className={styles.footerNextContainer}>
          {message}
          <Button
            data-save-button
            onClick={onNextWithLoading}
            ref={buttonRef}
            label={<Text t={nextTextKey} />}
            isLoading={isOnNextPending || projectSavePending || isLoading}
            disabled={disableNext}
          />
        </div>
        {!disableBack && (
          <TextButton
            data-back-button
            disabled={projectSavePending || isLoading}
            onClick={onBack}
            label={<Text t={backTextKey} />}
            // withSpinner
          />
        )}
      </FooterShell>
    </>
  );
};
Footer.propTypes = {
  nextTextKey: PropTypes.string,
  disableBack: PropTypes.bool,
};

export default Footer;
